/**
 * Display Manager Module
 * Church presentation and projection system - Admin control panel.
 * Sub-components are in ./DisplayManager/ folder.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AnimatePresence } from 'framer-motion';
import {
  Tv, Music, BookOpen, MessageSquare, Gift, Layout,
  Settings, Play, ListOrdered, Clock, Loader2, ExternalLink
} from 'lucide-react';

import ControlPanel from './DisplayManager/ControlPanel';
import WelcomeEditor from './DisplayManager/WelcomeEditor';
import HymnPanel from './DisplayManager/HymnPanel';
import BiblePanel from './DisplayManager/BiblePanel';
import AnnouncementPanel from './DisplayManager/AnnouncementPanel';
import OfferingPanel from './DisplayManager/OfferingPanel';
import PreServicePanel from './DisplayManager/PreServicePanel';
import ServiceFlowPanel from './DisplayManager/ServiceFlowPanel';
import DisplaySettings from './DisplayManager/DisplaySettings';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const tabs = [
  { id: 'control', label: 'Control', icon: Tv },
  { id: 'welcome', label: 'Welcome', icon: Layout },
  { id: 'preservice', label: 'Pre-Service', icon: Clock },
  { id: 'hymn', label: 'Hymns', icon: Music },
  { id: 'bible', label: 'Bible', icon: BookOpen },
  { id: 'announcement', label: 'Announcements', icon: MessageSquare },
  { id: 'offering', label: 'Offering', icon: Gift },
  { id: 'flow', label: 'Service Flow', icon: ListOrdered },
  { id: 'settings', label: 'Settings', icon: Settings },
];

const DisplayManagerModule = () => {
  const [activeTab, setActiveTab] = useState('control');
  const [settings, setSettings] = useState(null);
  const [currentDisplay, setCurrentDisplay] = useState(null);
  const [hymns, setHymns] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadData = async () => {
    try {
      const [settingsRes, displayRes, hymnsRes, announcementsRes] = await Promise.all([
        axios.get(`${API}/display/settings`),
        axios.get(`${API}/display/current`),
        axios.get(`${API}/hymns?language=english&limit=800`),
        axios.get(`${API}/display/announcements`)
      ]);
      setSettings(settingsRes.data);
      setCurrentDisplay(displayRes.data);
      setHymns(hymnsRes.data?.hymns || hymnsRes.data || []);
      setAnnouncements(announcementsRes.data || []);
    } catch (error) {
      console.error('Error loading display data:', error);
    }
    setIsLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Display Manager</h2>
          <p className="text-slate-500">Church presentation and projection system</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-xl">
            <div className={`w-2 h-2 rounded-full ${currentDisplay?.display_type !== 'blank' ? 'bg-green-500 animate-pulse' : 'bg-slate-400'}`} />
            <span className="text-sm font-medium text-slate-700">
              {currentDisplay?.display_type === 'blank' ? 'Screen Blank' : `Showing: ${currentDisplay?.display_type || 'None'}`}
            </span>
          </div>
          <button
            onClick={() => window.open('/media/live-display', '_blank')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Open Live Display
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        {activeTab === 'control' && <ControlPanel currentDisplay={currentDisplay} onRefresh={loadData} hymns={hymns} announcements={announcements} />}
        {activeTab === 'welcome' && <WelcomeEditor settings={settings} onSave={loadData} />}
        {activeTab === 'preservice' && <PreServicePanel onRefresh={loadData} />}
        {activeTab === 'hymn' && <HymnPanel hymns={hymns} onRefresh={loadData} />}
        {activeTab === 'bible' && <BiblePanel onRefresh={loadData} />}
        {activeTab === 'announcement' && <AnnouncementPanel announcements={announcements} onRefresh={loadData} />}
        {activeTab === 'offering' && <OfferingPanel onRefresh={loadData} />}
        {activeTab === 'flow' && <ServiceFlowPanel onRefresh={loadData} />}
        {activeTab === 'settings' && <DisplaySettings settings={settings} onSave={loadData} />}
      </div>
    </div>
  );
};

export default DisplayManagerModule;
